import { LitElement, html, nothing } from '../../vendor/lit/lit-core.esm.js?v=20260730a';
import {
  state,
  allCompanies,
  categoriesForCompany,
  systemsForCategory
} from './system_registry_core.js?v=20260904f';
import {
  activateSystem,
  clearSelectionForNavigation
} from './system_navigation.js?v=20260904f';
import { onViewportModeChange } from '../viewport/viewport_mode.js?v=20260731a';

// Kompakt-Modus-Hamburgermenue fuer die Header-Navigation (Firma -> Kategorie
// -> System), siehe project_responsive_mobile_20260731-Memo, Phase 1. Nur im
// Kompakt-Modus sichtbar (CSS gated ueber die von viewport_mode.js gesetzte
// html.viewport-compact-Klasse, siehe styles.css) — die Desktop-Switcher
// (#companySwitcher/#categorySwitcher/#systemSwitcher) bleiben unveraendert
// bestehen und werden per CSS nur ausgeblendet, keine Logik-Duplikation.
//
// Bewusst KEINE Abhaengigkeit von system_navigation.js zurueck zu diesem
// Modul (waere ein Zirkel, da diese Datei bereits activateSystem() etc. von
// dort importiert): statt dass renderShell() dieses Menue nach jeder
// Navigation aktiv benachrichtigt, liest render() `state` (ein veraenderliches
// Objekt aus system_registry_core.js) bei jedem Aufruf frisch — Lit re-rendert
// dieses Element ohnehin bei jeder eigenen Aktion (Menue oeffnen/schliessen,
// System waehlen). Die einzige echte Fremdaenderung, die synchronisiert
// werden muss, ist ein Wechsel VON Desktop-Breite (wo die eigentlichen
// Switcher aktiv sind) IN den Kompakt-Modus waehrend der Sitzung — dafuer
// genuegt ein Abo auf viewport_mode.js's onViewportModeChange (aus Phase 0).
//
// Drill-down statt alle 3 Ebenen gleichzeitig: eine Ebene nach der anderen,
// mit Zurueck-Button. Startet beim Oeffnen auf der System-Ebene der aktuell
// aktiven Firma/Kategorie (Systemwechsel ist die haeufigste Aktion), man
// kann aber jederzeit zurueck zu Kategorie/Firma. Ebenen mit nur einem
// Eintrag werden automatisch uebersprungen (vorwaerts beim Reinklicken,
// rueckwaerts beim Zurueck-Button).
//
// Zwischen-Taps (Firma/Kategorie) aendern NUR die lokale "browsing"-Position
// dieses Menues, nicht den echten App-State — sonst wuerde z.B. das Antippen
// einer Firma sofort ein System laden, nur um es beim naechsten Tap wieder zu
// wechseln. Erst der Tap auf ein System ruft activateSystem() auf (das sich
// bei Bedarf selbst um Firma/Kategorie kuemmert, siehe system_navigation.js)
// und schliesst das Menue.

class KsHeaderNavMenu extends LitElement {
  static properties = {
    open: { type: Boolean },
    level: { attribute: false },
    browsingCompanyId: { attribute: false },
    browsingCategoryId: { attribute: false }
  };

  createRenderRoot() {
    return this;
  }

  constructor() {
    super();
    this.open = false;
    this.level = 'system';
    this.browsingCompanyId = null;
    this.browsingCategoryId = null;
    this._unsubscribeViewportMode = null;
    this._systemSwitcherObserver = null;
  }

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener('keydown', this._onKeydown);
    document.addEventListener('pointerdown', this._onOutsidePointerDown);
    this._unsubscribeViewportMode = onViewportModeChange(isCompact => {
      if (isCompact) this.requestUpdate();
    });
    // Der allererste render() laeuft moeglicherweise, bevor
    // SystemRegistry.renderShell() Firma/Kategorie/System das erste Mal
    // befuellt hat (Registry-Daten laden asynchron beim Boot) — ohne diesen
    // Beobachter bliebe die "aktuell gewaehlt"-Beschriftung dauerhaft auf
    // dem leeren Anfangszustand haengen, bis der Nutzer zufaellig selbst
    // eine eigene reaktive Property des Menues aendert (z.B. Menue oeffnen).
    // #systemSwitcher wird von renderShell() bei jeder Navigations-Aenderung
    // per innerHTML neu befuellt — das reicht als zuverlaessiges, komplett
    // eigenstaendiges Signal, ohne system_navigation.js direkt zu koppeln
    // (das wuerde einen Importzirkel ergeben, siehe Kommentar oben).
    const systemSwitcher = document.getElementById('systemSwitcher');
    if (systemSwitcher) {
      this._systemSwitcherObserver = new MutationObserver(() => this.requestUpdate());
      this._systemSwitcherObserver.observe(systemSwitcher, { childList: true, subtree: true });
    }
  }

  disconnectedCallback() {
    document.removeEventListener('keydown', this._onKeydown);
    document.removeEventListener('pointerdown', this._onOutsidePointerDown);
    this._unsubscribeViewportMode?.();
    this._systemSwitcherObserver?.disconnect();
    super.disconnectedCallback();
  }

  _onKeydown = event => {
    if (event.key === 'Escape' && this.open) this._close();
  }

  _onOutsidePointerDown = event => {
    if (!this.open || this.contains(event.target)) return;
    this._close();
  }

  _resetBrowsingToActive() {
    this.browsingCompanyId = state.activeCompanyId;
    this.browsingCategoryId = state.activeCategoryId;
    this.level = 'system';
  }

  _toggleOpen = () => {
    if (this.open) {
      this._close();
      return;
    }
    this._resetBrowsingToActive();
    this.open = true;
  }

  _close = () => {
    this.open = false;
  }

  _enterCategoryLevel(companyId) {
    this.browsingCompanyId = companyId;
    const categories = categoriesForCompany(companyId);
    if (categories.length > 1) {
      this.browsingCategoryId = categories[0]?.id ?? null;
      this.level = 'category';
      return;
    }
    this._enterSystemLevel(categories[0]?.id ?? null);
  }

  _enterSystemLevel(categoryId) {
    this.browsingCategoryId = categoryId;
    const systems = systemsForCategory(categoryId);
    if (systems.length === 1) {
      this._selectSystem(systems[0].id);
      return;
    }
    this.level = 'system';
  }

  _selectCompany = companyId => {
    this._enterCategoryLevel(companyId);
  }

  _selectCategory = categoryId => {
    this._enterSystemLevel(categoryId);
  }

  _selectSystem = systemId => {
    clearSelectionForNavigation(state.activeSystemId, systemId);
    activateSystem(systemId, { refreshView: true });
    this._close();
  }

  _goBack = () => {
    if (this.level === 'system') {
      this.level = categoriesForCompany(this.browsingCompanyId).length > 1 ? 'category' : 'company';
    } else if (this.level === 'category') {
      this.level = 'company';
    }
  }

  _renderBreadcrumb() {
    const company = allCompanies().find(entry => entry.id === this.browsingCompanyId);
    const category = categoriesForCompany(this.browsingCompanyId).find(entry => entry.id === this.browsingCategoryId);
    const title = this.level === 'company' ? 'Firma' : this.level === 'category' ? 'Bereich' : 'System';
    const pathParts = [];
    if (this.level !== 'company') pathParts.push(company?.label);
    if (this.level === 'system') pathParts.push(category?.label);
    const path = pathParts.filter(Boolean).join(' › ');
    return html`
      <div class="header-nav-menu-crumb">
        ${this.level !== 'company' ? html`<button type="button" class="header-nav-menu-back" @click=${this._goBack} aria-label="Zurück">‹</button>` : nothing}
        <span class="header-nav-menu-crumb-title">${title}</span>
        ${path ? html`<span class="header-nav-menu-crumb-path">${path}</span>` : nothing}
        <button type="button" class="header-nav-menu-close" @click=${this._close} aria-label="Schliessen">✕</button>
      </div>
    `;
  }

  _renderList() {
    if (this.level === 'company') {
      return html`
        <ul class="header-nav-menu-list">
          ${allCompanies().map(company => html`
            <li>
              <button type="button" class="header-nav-menu-row${company.id === state.activeCompanyId ? ' is-active' : ''}" @click=${() => this._selectCompany(company.id)}>
                <span>${company.label}</span>
                <span class="header-nav-menu-row-arrow" aria-hidden="true">›</span>
              </button>
            </li>
          `)}
        </ul>
      `;
    }
    if (this.level === 'category') {
      return html`
        <ul class="header-nav-menu-list">
          ${categoriesForCompany(this.browsingCompanyId).map(category => html`
            <li>
              <button type="button" class="header-nav-menu-row${category.id === state.activeCategoryId ? ' is-active' : ''}" @click=${() => this._selectCategory(category.id)}>
                <span>${category.label}</span>
                <span class="header-nav-menu-row-arrow" aria-hidden="true">›</span>
              </button>
            </li>
          `)}
        </ul>
      `;
    }
    return html`
      <ul class="header-nav-menu-list">
        ${systemsForCategory(this.browsingCategoryId).map(system => html`
          <li>
            <button type="button" class="header-nav-menu-row${system.id === state.activeSystemId ? ' is-active' : ''}" @click=${() => this._selectSystem(system.id)}>
              <span>${system.label}</span>
            </button>
          </li>
        `)}
      </ul>
    `;
  }

  _renderCurrentLabel() {
    const category = categoriesForCompany(state.activeCompanyId).find(entry => entry.id === state.activeCategoryId);
    const system = systemsForCategory(state.activeCategoryId).find(entry => entry.id === state.activeSystemId);
    return html`
      <span class="header-nav-menu-current-category">${category?.label || ''}</span>
      <span class="header-nav-menu-current-system">${system?.label || 'Navigation'}</span>
    `;
  }

  render() {
    return html`
      <button type="button" class="header-nav-menu-toggle" aria-label="Navigation" aria-expanded=${this.open ? 'true' : 'false'} @click=${this._toggleOpen}>
        <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 6h16M4 12h16M4 18h16" /></svg>
      </button>
      <button type="button" class="header-nav-menu-current" @click=${this._toggleOpen}>
        ${this._renderCurrentLabel()}
      </button>
      ${this.open ? html`
        <div class="header-nav-menu-panel" role="dialog" aria-label="Navigation">
          ${this._renderBreadcrumb()}
          ${this._renderList()}
        </div>
      ` : nothing}
    `;
  }
}

customElements.define('ks-header-nav-menu', KsHeaderNavMenu);
